/**
 * BuilderFormFields Component
 * Delade formulärfält för Add/Update Builder
 * Endast presentation - ingen egen state eller logik
 */

import React from 'react';
import FormInput from '../../../components/common/FormInput';

export const BuilderFormFields = ({
  values,
  setValues,
  errors,
  previewUrl,
  handleChange,
  handleAvatarChange,
  handleRemoveAvatar,
  isUpdate,
  connectedUsers = [],
  onSetContactPerson,
  onSetPuPriorityContact,
  showYrkesgruppPricing = false
}) => {
  return (
    <>
      {/* Grundläggande information */}
      <FormInput
        label="Företagsnamn"
        name="companyName"
        value={values.companyName}
        onChange={handleChange}
        required
        placeholder="Ange företagsnamn"
      />

      {/* Kortnamnet står i faktureringsunderlaget i stället för hela
          företagsnamnet. Tomt = företagsnamnet utan "AB". */}
      <FormInput
        label="Kortnamn"
        name="kortnamn"
        value={values.kortnamn}
        onChange={handleChange}
        placeholder={values.companyName ? values.companyName.replace(/\s+AB$/i, '') : 'T.ex. RosaSågen'}
      />
      <p className="-mt-2 mb-2 text-xs text-gray-500">
        Används i faktureringsunderlaget. Lämnas tomt används företagsnamnet utan "AB".
      </p>

      <FormInput
        label="Organisationsnummer"
        name="organizationNumber"
        value={values.organizationNumber}
        onChange={handleChange}
        placeholder="XXXXXX-XXXX"
        pattern="[0-9]{6}-?[0-9]{4}"
        title="Format: XXXXXX-XXXX"
      />

      <FormInput
        label="Hemsida"
        type="url"
        name="website"
        value={values.website}
        onChange={handleChange}
        placeholder="https://exempel.se"
      />

      {/* Fritext, inte type="url" — de flesta skriver @namn och webbläsarens
          URL-validering hade underkänt det. Normaliseras vid visning. */}
      <FormInput
        label="Instagram"
        name="instagram"
        value={values.instagram}
        onChange={handleChange}
        placeholder="@foretaget"
      />

      {/* Adress */}
      <FormInput
        label="Gatuadress"
        name="addressStreet"
        value={values.addressStreet}
        onChange={handleChange}
        placeholder="Exempelgatan 123"
      />
      <div className="grid grid-cols-2 gap-4">
        <FormInput
          label="Postnummer"
          name="addressZipCode"
          value={values.addressZipCode}
          onChange={handleChange}
          placeholder="123 45"
        />
        <FormInput
          label="Postort"
          name="addressCity"
          value={values.addressCity}
          onChange={handleChange}
          placeholder="Stockholm"
        />
      </div>

      {/* Kontaktpersoner (endast vid uppdatering) */}
      {isUpdate && connectedUsers.length > 0 && (
        <>
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Kontaktperson
            </label>
            <select
              value={values.contactPerson}
              onChange={(e) => onSetContactPerson(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="">Välj kontaktperson</option>
              {connectedUsers.map(user => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Extra kontaktperson - visa endast om det finns fler än 1 användare */}
          {connectedUsers.length > 1 && (
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                Extra kontaktperson
              </label>
              <select
                value={values.additionalContactPersons[0] || ""}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  setValues(prev => ({
                    ...prev,
                    additionalContactPersons: selectedId ? [selectedId] : []
                  }));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Välj extra kontaktperson</option>
                {connectedUsers
                  .filter(user => user._id !== values.contactPerson)
                  .map(user => (
                    <option key={user._id} value={user._id}>
                      {user.firstName} {user.lastName}
                    </option>
                  ))}
              </select>
            </div>
          )}

          {/* PU-prioriterad kontaktperson */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Prioriterad för PU-mottagning
              <span className="text-xs text-gray-500 block mt-1">
                Denna person kommer att väljas först när projektunderlag skickas
              </span>
            </label>
            <select
              value={values.puPriorityContact}
              onChange={(e) => onSetPuPriorityContact(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
            >
              <option value="">Använd huvudkontaktperson som standard</option>
              {connectedUsers.map(user => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                  {user._id === values.contactPerson && ' (Huvudkontakt)'}
                  {values.additionalContactPersons.includes(user._id) && ' (Extra kontakt)'}
                </option>
              ))}
            </select>
            {values.puPriorityContact && (
              <div className="text-sm text-green-600 mt-1">
                ✓ {connectedUsers.find(u => u._id === values.puPriorityContact)?.firstName} {connectedUsers.find(u => u._id === values.puPriorityContact)?.lastName} kommer att prioriteras för PU-mottagning
              </div>
            )}
          </div>
        </>
      )}

      {/* Avatar upload */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Profilbild
        </label>
        <div className="flex items-center space-x-4">
          <div className="relative w-16 h-16">
            {previewUrl && (
              <>
                <img
                  src={previewUrl}
                  alt="Avatar preview"
                  className="w-16 h-16 rounded-full object-cover"
                />
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="absolute -top-2 -right-2 bg-leadme-red text-white rounded-full p-1 hover:bg-leadme-red-dark"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </>
            )}
          </div>
          <div>
            <input
              type="file"
              accept="image/*"
              id="avatar-upload"
              onChange={e => {
                const file = e.target.files[0];
                if (file) handleAvatarChange(file);
              }}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-leadme-pink-light file:text-white hover:file:bg-leadme-pink"
              aria-label="Ladda upp ny profilbild"
              tabIndex={0}
            />
          </div>
        </div>
        {errors.avatar && (
          <p className="text-red-500 text-sm mt-1">{errors.avatar}</p>
        )}
      </div>
    </>
  );
};

